import { apiClient, getActivePvzId } from './_shared.js'

// ============================================
// SALARY CONFIG
// ============================================

// SALARY CONFIG CACHE
let cachedSalaryConfig = {}
let pvzTimezoneCache = {}

export async function getSalaryConfig(pvzId) {
  const effectivePvzId = pvzId || getActivePvzId()
  if (!effectivePvzId) return {}

  // Для мульти-выбора ПВЗ берём первый ID для определения базовой таймзоны
  const primaryPvzId = String(effectivePvzId).split(',')[0].trim()

  let tzPromise = Promise.resolve(pvzTimezoneCache[primaryPvzId] || null)
  if (primaryPvzId && !pvzTimezoneCache[primaryPvzId]) {
    tzPromise = apiClient(`/pvz/${primaryPvzId}`)
      .then(p => {
        const tz = p?.timezone || null
        if (tz) pvzTimezoneCache[primaryPvzId] = tz
        return tz
      })
      .catch(() => null)
  }

  const [data, tz] = await Promise.all([
    apiClient(`/salary-config?pvzId=${effectivePvzId}`),
    tzPromise
  ])

  return normalizeSalaryConfigData(data, tz)
}

/**
 * Чистая функция нормализации конфигурации зарплат из DB-формата (snake_case)
 * или уже нормализованного формата в каноническую структуру frontend (camelCase)
 */
export function normalizeSalaryConfigData(data, tz = null) {
  if (!data || Object.keys(data).length === 0) return {}

  const rules = data.custom_rules || data.customRules || []
  const showSalaryRule = rules.find(r => r.id === 'system:show_salary_to_admins')
  const autoCalcRule = rules.find(r => r.id === 'system:wb_revenue_auto_calc')
  const calcPeriodRule = rules.find(r => r.id === 'system:wb_revenue_calc_period')
  const bonusPayoutMonthModeRule = rules.find(r => r.id === 'system:bonus_payout_month_mode')

  const effectiveTz = tz || data.timezone || data.pvz_timezone || null

    const rawHelperDeduction = data.helper_deduction_percent ?? data.helperDeductionPercent
    const helperDeductionPercent = (rawHelperDeduction !== undefined && rawHelperDeduction !== null && !isNaN(Number(rawHelperDeduction)))
      ? Number(rawHelperDeduction)
      : 50

    const rawWbRevenue = data.wb_revenue_per_issued ?? data.wbRevenuePerIssued
    const wbRevenuePerIssued = (rawWbRevenue !== undefined && rawWbRevenue !== null && !isNaN(Number(rawWbRevenue)))
      ? Number(rawWbRevenue)
      : 15.0

    return {
      id: data.id,
      pvzId: data.pvz_id || data.pvzId,
      pvz_id: data.pvz_id || data.pvzId,
      timezone: effectiveTz,
      pvz_timezone: effectiveTz,
      hourlyRate: Number(data.hourly_rate ?? data.hourlyRate) || 0,
      dailyRate: Number(data.daily_rate ?? data.dailyRate) || 0,
      bonusPerAccepted: Number(data.bonus_per_accepted ?? data.bonusPerAccepted) || 0,
      bonusPerIssued: Number(data.bonus_per_issued ?? data.bonusPerIssued) || 0,
      bonusPerReturned: Number(data.bonus_per_returned ?? data.bonusPerReturned) || 0,
      revenuePercent: Number(data.revenue_percent ?? data.revenuePercent) || 0,
      turnoverPercent: Number(data.turnover_percent ?? data.turnoverPercent) || 0,
      helperHourlyRate: Number(data.helper_hourly_rate ?? data.helperHourlyRate) || 0,
      helperDeductionPercent,
      helperBonusPerAccepted: Number(data.helper_bonus_per_accepted ?? data.helperBonusPerAccepted) || 0,
      helperBonusPerIssued: Number(data.helper_bonus_per_issued ?? data.helperBonusPerIssued) || 0,
      helperBonusPerReturned: Number(data.helper_bonus_per_returned ?? data.helperBonusPerReturned) || 0,
      helperOwnBonusPerAccepted: Number(data.helper_own_bonus_per_accepted ?? data.helperOwnBonusPerAccepted) || 0,
      helperOwnBonusPerIssued: Number(data.helper_own_bonus_per_issued ?? data.helperOwnBonusPerIssued) || 0,
      helperOwnBonusPerReturned: Number(data.helper_own_bonus_per_returned ?? data.helperOwnBonusPerReturned) || 0,
      customRules: rules.filter(r => !r.id?.startsWith('system:')).map(r => ({ ...r, amount: Number(r.amount) || 0 })),
      showSalaryToAdmins: showSalaryRule ? showSalaryRule.value : Boolean(data.showSalaryToAdmins),
      payoutFrequency: (data.payout_frequency || data.payoutFrequency || 'weekly') === 'biweekly' ? 'bi_monthly' : (data.payout_frequency || data.payoutFrequency || 'weekly'),
      payoutDay: data.payout_day || data.payoutDay || 1,
      enableHelpers: data.enable_helpers ?? data.enableHelpers ?? true,
      wbRevenuePerIssued,
      hideArchiveInSalary: data.hide_archive_in_salary ?? data.hideArchiveInSalary ?? true,
      payBonusesMonthly: data.pay_bonuses_monthly ?? data.payBonusesMonthly ?? false,
      bonusPayoutDay: data.bonus_payout_day || data.bonusPayoutDay || 5,
      wbRevenueAutoCalc: autoCalcRule ? autoCalcRule.value : Boolean(data.wbRevenueAutoCalc),
      wbRevenueCalcPeriod: calcPeriodRule ? calcPeriodRule.value : (data.wbRevenueCalcPeriod || 'last_month'),
      bonusPayoutMonthMode: bonusPayoutMonthModeRule ? bonusPayoutMonthModeRule.value : (data.bonusPayoutMonthMode || 'next'),
      bonusCalculationMethod: data.bonus_calculation_method || data.bonusCalculationMethod || 'individual',
    payoutShiftsCount: Number(data.payout_shifts_count ?? data.payoutShiftsCount) || 10,
    dailyRateMode: data.daily_rate_mode || data.dailyRateMode || 'config',
    settlementCutoverDate: (data.settlement_cutover_date || data.settlementCutoverDate) ? String(data.settlement_cutover_date || data.settlementCutoverDate).split('T')[0] : null,
    settlement_cutover_date: (data.settlement_cutover_date || data.settlementCutoverDate) ? String(data.settlement_cutover_date || data.settlementCutoverDate).split('T')[0] : null
  }
}

function normalizeSalaryConfigForSave(config) {
  const num = (v) => {
    if (v === undefined || v === null || v === '') return 0
    const parsed = parseFloat(v)
    return isNaN(parsed) ? 0 : parsed
  }

  const normCustomRules = (config.customRules || []).map(r => ({
    ...r,
    amount: num(r.amount)
  }))

  const rawSaveFreq = config.payoutFrequency || config.payout_frequency || 'weekly'
  const payoutFrequency = rawSaveFreq === 'biweekly' ? 'bi_monthly' : rawSaveFreq

  return {
    ...config,
    payoutFrequency,
    payout_frequency: payoutFrequency,
    dailyRate: num(config.dailyRate),
    hourlyRate: num(config.hourlyRate),
    bonusPerAccepted: num(config.bonusPerAccepted),
    bonusPerIssued: num(config.bonusPerIssued),
    bonusPerReturned: num(config.bonusPerReturned),
    turnoverPercent: num(config.turnoverPercent),
    helperHourlyRate: num(config.helperHourlyRate),
    helperDeductionPercent: num(config.helperDeductionPercent),
    helperOwnBonusPerAccepted: num(config.helperOwnBonusPerAccepted),
    helperOwnBonusPerIssued: num(config.helperOwnBonusPerIssued),
    helperOwnBonusPerReturned: num(config.helperOwnBonusPerReturned),
    payoutShiftsCount: num(config.payoutShiftsCount) || 10,
    payoutDay: num(config.payoutDay),
    customRules: normCustomRules,
    settlementCutoverDate: (config.settlementCutoverDate || config.settlement_cutover_date) ? String(config.settlementCutoverDate || config.settlement_cutover_date).split('T')[0] : null,
    settlement_cutover_date: (config.settlementCutoverDate || config.settlement_cutover_date) ? String(config.settlementCutoverDate || config.settlement_cutover_date).split('T')[0] : null
  }
}

function attachSystemRules(normalized, config) {
  const customRules = [...(normalized.customRules || [])]
  if (config.showSalaryToAdmins && !customRules.some(r => r.id === 'system:show_salary_to_admins')) {
    customRules.push({ id: 'system:show_salary_to_admins', value: true })
  }
  if (config.wbRevenueAutoCalc && !customRules.some(r => r.id === 'system:wb_revenue_auto_calc')) {
    customRules.push({ id: 'system:wb_revenue_auto_calc', value: true })
  }
  if (config.wbRevenueCalcPeriod && !customRules.some(r => r.id === 'system:wb_revenue_calc_period')) {
    customRules.push({ id: 'system:wb_revenue_calc_period', value: config.wbRevenueCalcPeriod })
  }
  if (config.bonusPayoutMonthMode && !customRules.some(r => r.id === 'system:bonus_payout_month_mode')) {
    customRules.push({ id: 'system:bonus_payout_month_mode', value: config.bonusPayoutMonthMode })
  }
  return customRules
}

export async function updateSalaryConfig(config, userId) {
  const normalized = normalizeSalaryConfigForSave(config)
  const customRules = attachSystemRules(normalized, config)

  const effectivePvzId = config.pvzId || config.pvz_id || getActivePvzId()
  const payload = {
    ...normalized,
    pvzId: effectivePvzId,
    pvz_id: effectivePvzId,
    customRules
  }

  return await apiClient('/salary-config', {
    method: 'POST',
    body: JSON.stringify({ config: payload, userId, pvz_id: effectivePvzId })
  })
}

export async function getSalaryConfigHistory(pvzId) {
  const effectivePvzId = pvzId || getActivePvzId()
  return await apiClient(`/salary-config/history?pvzId=${effectivePvzId}`)
}

export async function saveSalaryConfigWithDate(config, effectiveDate, userId) {
  const normalized = normalizeSalaryConfigForSave(config)
  const customRules = attachSystemRules(normalized, config)
  const effectivePvzId = config.pvzId || config.pvz_id || getActivePvzId()
  const payload = {
    ...normalized,
    pvzId: effectivePvzId,
    pvz_id: effectivePvzId,
    customRules
  }
  return await apiClient('/salary-config/with-date', {
    method: 'POST',
    body: JSON.stringify({ config: payload, effectiveDate, userId, pvz_id: effectivePvzId })
  })
}

export async function getMissingTurnoverDays(daysToCheck = 3) {
  return await apiClient(`/salary-config/missing-turnover?daysToCheck=${daysToCheck}`)
}

export async function saveDailyTurnover(date, amount, userId) {
  const pvzId = getActivePvzId()
  return await apiClient('/salary-config/daily-turnover', {
    method: 'POST',
    body: JSON.stringify({ pvz_id: pvzId, turnovers: [{ date, amount }] })
  })
}

export function getNextPayDayDate(payDay) {
  if (payDay == null) return null
  const now = new Date()
  const current = new Date(now)
  for (let i = 0; i < 7; i++) {
    current.setDate(now.getDate() + i)
    if (current.getDay() === payDay) {
      return current
    }
  }
  return now
}
